import React, { useContext, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import NoteContext from "../../../context/Notes/NoteContext";
import "./LandingPage.css";
import ParticleSimulation from "../../../components/ParticleSimulation/ParticleSimulation";

const NOW_ITEMS = [
  {
    label: "Building",
    value: "√2 Technologies — a personal publishing platform",
  },
  {
    label: "Reading",
    value: "The Emperor of All Maladies · Siddhartha Mukherjee",
  },
  { label: "Listening", value: "Hey Now — London Grammar (2013)" },
  { label: "Location", value: "Hyderabad, India" },
];

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

/* ── helpers ── */
function readCache(key) {
  try { return JSON.parse(localStorage.getItem(key)) || null; } catch { return null; }
}
function writeCache(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

const LandingPage = () => {
  const { allNotes, recentPosts, initialLoadDone } = useContext(NoteContext);

  /* Photos: seed from cache instantly, refresh in background */
  const [recentPhotos, setRecentPhotos] = useState(() => readCache("sq2_recent_photos") || []);
  const [photosLoaded, setPhotosLoaded] = useState(() => !!readCache("sq2_recent_photos"));

  const host = process.env.REACT_APP_BACKEND || "http://localhost:5000";

  /* Stale-while-revalidate for photos — cache makes it instant on return visits */
  useEffect(() => {
    fetch(`${host}/api/photos/recent?limit=6`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.photos?.length) {
          setRecentPhotos(data.photos);
          writeCache("sq2_recent_photos", data.photos);
        }
      })
      .catch(() => {})
      .finally(() => setPhotosLoaded(true));
  }, [host]);

  /* pick the 4 most recent posts to show in the writing section */
  const writingItems = (
    recentPosts && recentPosts.length > 0 ? recentPosts : allNotes
  ).slice(0, 4);

  return (
    <div className="lp-root">
      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-wrap">
          <div className="lp-hero-grid">
            <div>
              <span className="lp-hero-tag">
                WRITEs | talks code | PHOTOGRAPHs
              </span>
              <h1 className="lp-hero-headline">
                Ideas worth sitting
                <br />
                with for a <em>while</em>.
              </h1>
              <p className="lp-hero-desc">
                Writing about building software, the texture of ideas, and what
                it feels like to make things carefully. Based in Hyderabad, India,
                shipping slowly on purpose.
              </p>
              <div className="lp-hero-meta">
                <Link to="/home" className="lp-hero-cta">
                  Read all posts →
                </Link>
                <span className="lp-hero-reading">
                  Reading: <span>The Emperor of All Maladies</span>
                </span>
              </div>
            </div>
            <div className="lp-photo-placeholder" aria-label="Particle simulation">
              <ParticleSimulation />
            </div>
          </div>
        </div>
      </section>

      {/* ── Writing ── */}
      <section className="lp-section" aria-labelledby="writing-heading">
        <div className="lp-wrap">
          <div className="lp-section-header">
            <h2 className="lp-section-title" id="writing-heading">
              Writing
            </h2>
            <Link to="/home" className="lp-section-all">
              All posts →
            </Link>
          </div>
          <ul className="lp-article-list">
            {!initialLoadDone && recentPosts.length === 0
              ? Array(4).fill(null).map((_, i) => (
                  <li key={i}>
                    <div className="lp-article-item lp-shimmer-row">
                      <span className="lp-shimmer" style={{ height: 12, width: 48, display: "block", borderRadius: 2 }} />
                      <div>
                        <div className="lp-shimmer" style={{ height: 18, borderRadius: 2, width: "70%", marginBottom: 8 }} />
                        <div className="lp-shimmer" style={{ height: 11, borderRadius: 2, width: "30%" }} />
                      </div>
                    </div>
                  </li>
                ))
              : writingItems.map((note) => (
                  <li key={note._id}>
                    <Link to={`/blog/${note._id}`} className="lp-article-item">
                      <span className="lp-article-tag">
                        {note.type || note.tag || "Post"}
                      </span>
                      <div>
                        <h3 className="lp-article-title">{note.title}</h3>
                        <div className="lp-article-meta">
                          <span>{formatDate(note.date)}</span>
                          {note.readTimeMinutes && (
                            <span>{note.readTimeMinutes} min</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
            {initialLoadDone && writingItems.length === 0 && (
              <li
                style={{
                  padding: "2rem 0",
                  color: "var(--text3)",
                  fontSize: "0.9rem",
                  fontStyle: "italic",
                }}
              >
                No posts yet.
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* ── Photography ── */}
      <section
        className="lp-section"
        id="photography"
        aria-labelledby="photos-heading"
      >
        <div className="lp-wrap">
          <div className="lp-section-header">
            <h2 className="lp-section-title" id="photos-heading">
              Photography
            </h2>
            <Link to="/photos" className="lp-section-all">
              All photos →
            </Link>
          </div>
          <div className="lp-photo-grid">
            {recentPhotos.length > 0
              ? recentPhotos.map((photo) => (
                  <Link
                    key={photo._id}
                    to="/photos"
                    className="lp-photo-cell"
                    aria-label={photo.title || "Photo"}
                  >
                    <img
                      src={photo.url}
                      alt={photo.title || photo.caption || "Photo"}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: "var(--radius)" }}
                    />
                  </Link>
                ))
              : !photosLoaded
                ? Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="lp-photo-cell lp-shimmer" aria-hidden="true" />
                  ))
                : null
            }
          </div>
        </div>
      </section>

      {/* ── Now ── */}
      <section className="lp-section" id="now" aria-labelledby="now-heading">
        <div className="lp-wrap">
          <div className="lp-section-header">
            <h2 className="lp-section-title" id="now-heading">
              Now
            </h2>
          </div>
          <div className="lp-now">
            <p className="lp-now-title">What I'm doing right now</p>
            <ul className="lp-now-list">
              {NOW_ITEMS.map(({ label, value }) => (
                <li key={label} className="lp-now-item">
                  <span className="lp-now-label">{label}</span>
                  <span className="lp-now-value">{value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <div className="lp-wrap">
        <div className="lp-footer-inner">
          <span className="lp-footer-copy">
            © {new Date().getFullYear()} Tanvir Raihan Islam
          </span>
          <nav className="lp-footer-links" aria-label="Footer">
            <Link to="/home" className="lp-footer-link">
              Writing
            </Link>
            <Link to="/photos" className="lp-footer-link">
              Photos
            </Link>
            <Link to="/login" className="lp-footer-link">
              Sign in
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
